import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCapsules } from '../../hooks/useCapsules';
import { CapsuleCard } from '../../components/CapsuleCard';
import { DeleteConfirmDialog } from '../../components/DeleteConfirmDialog';
import { Toast } from '../../components/Toast';
import { Capsule } from '../../types/capsule';
import '../../styles/CapsuleListPage.css';

type SortOption = 'title' | 'recently-edited';

export function CapsuleListPage() {
  const navigate = useNavigate();
  const { capsules, loading, error } = useCapsules();
  const [deleteTarget, setDeleteTarget] = useState<Capsule | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('recently-edited');

  const sortedCapsules = useMemo(() => {
    const sorted = [...capsules];
    if (sortBy === 'recently-edited') {
      sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else {
      sorted.sort((a, b) => a.metadata.title.localeCompare(b.metadata.title));
    }
    return sorted;
  }, [capsules, sortBy]);

  const handleEdit = (capsule: Capsule) => {
    navigate(`/admin/curate?edit=${capsule.slug}`);
  };

  const handleDelete = (capsule: Capsule) => {
    setDeleteTarget(capsule);
  };

  const handleConfirmDelete = () => {
    const title = deleteTarget?.metadata.title;
    setDeleteTarget(null);
    setToast({ message: `Delete not yet implemented for "${title}"`, type: 'error' });
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
  };

  const handleNewCapsule = () => {
    navigate('/admin/curate');
  };

  if (loading) {
    return (
      <div className="capsule-list-page">
        <div className="capsule-list-loading">Loading capsules...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="capsule-list-page">
        <div className="capsule-list-error">
          {error}
          <button
            className="btn btn-secondary"
            style={{ marginTop: 'var(--space-md)', display: 'block', marginInline: 'auto' }}
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="capsule-list-page">
      <div className="capsule-list-header">
        <h1 className="capsule-list-title">Manage Capsules</h1>
        <div className="capsule-list-controls">
          <div className="sort-control">
            <label htmlFor="sort-select">Sort by:</label>
            <select
              id="sort-select"
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
            >
              <option value="recently-edited">Recently edited</option>
              <option value="title">Title</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleNewCapsule}>
            + New Capsule
          </button>
        </div>
      </div>

      {capsules.length === 0 ? (
        <div className="capsule-list-empty">
          <p className="empty-message">No capsules found</p>
          <p className="empty-hint">Click "New Capsule" to create your first capsule.</p>
          <button className="btn btn-primary" onClick={handleNewCapsule}>
            Create First Capsule
          </button>
        </div>
      ) : (
        <div className="capsule-grid">
          {sortedCapsules.map((capsule) => (
            <CapsuleCard
              key={capsule.id}
              capsule={capsule}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <DeleteConfirmDialog
        isOpen={deleteTarget !== null}
        title={deleteTarget?.metadata.title || ''}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
